import type { PaginatedResponse, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import type { TopupInput } from "../types/sms.types";
import {
	type BackendBalanceTransactionsResponse,
	type BackendSmsAutoSettings,
	type BackendSmsBalance,
	type BackendSmsDetalizationItem,
	type BackendSmsLog,
	type BackendSmsReport,
	type BackendSmsTemplate,
	mapBackendBalance,
	mapBackendDetalizationItem,
	mapBackendReport,
	mapBackendTransaction,
	toTopUpRequest,
} from "./sms.mapper";

export type BalanceTransactionsListParams = {
	type?: "topup" | "spend" | "refund";
	date_from?: string;
	date_to?: string;
	page?: number;
	limit?: number;
};

export type SmsTemplatesListParams = {
	search?: string;
	type?: string;
	page?: number;
	limit?: number;
};

export type SmsLogsListParams = {
	clientId?: string;
	status?: string;
	dateFrom?: string;
	dateTo?: string;
	page?: number;
	limit?: number;
};

export async function getSmsBalance() {
	const response = await apiClient.get<SuccessResponse<BackendSmsBalance>>(endpoints.sms.balance);
	return mapBackendBalance(response.data.data);
}

export async function topupSmsBalance(input: TopupInput) {
	const response = await apiClient.post<SuccessResponse<BackendSmsBalance>>(
		endpoints.sms.topup,
		toTopUpRequest(input)
	);
	return mapBackendBalance(response.data.data);
}

export async function getBalanceTransactions(params?: BalanceTransactionsListParams) {
	const response = await apiClient.get<SuccessResponse<BackendBalanceTransactionsResponse>>(
		endpoints.sms.transactions,
		{ params }
	);
	const data = response.data.data;
	return {
		items: data.items.map(mapBackendTransaction),
		meta: data.meta,
	};
}

export async function getSmsTemplates(params?: SmsTemplatesListParams) {
	const response = await apiClient.get<PaginatedResponse<BackendSmsTemplate>>(
		endpoints.sms.templates,
		{ params }
	);
	return response.data.data;
}

export async function createSmsTemplate(data: {
	name: string;
	code: string;
	body: string;
	type?: "custom" | "system" | "payment_reminder" | "overdue" | "welcome";
	isActive?: boolean;
}) {
	const response = await apiClient.post<SuccessResponse<BackendSmsTemplate>>(
		endpoints.sms.templates,
		data
	);
	return response.data.data;
}

export async function updateSmsTemplate(
	id: string,
	data: Partial<{
		name: string;
		code: string;
		body: string;
		type: "custom" | "system" | "payment_reminder" | "overdue" | "welcome";
		isActive: boolean;
	}>
) {
	const response = await apiClient.patch<SuccessResponse<BackendSmsTemplate>>(
		endpoints.sms.templateDetail(id),
		data
	);
	return response.data.data;
}

export async function deleteSmsTemplate(id: string) {
	await apiClient.delete<void>(endpoints.sms.templateDetail(id));
}

export async function getSmsLogs(params?: SmsLogsListParams) {
	const response = await apiClient.get<PaginatedResponse<BackendSmsLog>>(endpoints.sms.logs, {
		params,
	});
	return response.data.data;
}

export async function getSmsAutoSettings() {
	const response = await apiClient.get<SuccessResponse<BackendSmsAutoSettings>>(
		endpoints.sms.autoSettings
	);
	return response.data.data;
}

export async function updateSmsAutoSettings(data: {
	isEnabled?: boolean;
	daysBefore?: number[];
	daysAfter?: number[];
	reminderTemplateId?: string | null;
	overdueTemplateId?: string | null;
}) {
	const response = await apiClient.patch<SuccessResponse<BackendSmsAutoSettings>>(
		endpoints.sms.autoSettings,
		data
	);
	return response.data.data;
}

export type SendSmsPayload = {
	clientId: string;
	templateId?: string;
	messageBody: string;
	phoneNumber?: string;
};

export async function sendSms(payload: SendSmsPayload) {
	const response = await apiClient.post<SuccessResponse<BackendSmsLog>>(
		endpoints.sms.send,
		payload
	);
	return response.data.data;
}

export type SendBulkSmsPayload = {
	clientIds: string[];
	templateId?: string;
	messageBody: string;
};

export async function sendBulkSms(payload: SendBulkSmsPayload) {
	const response = await apiClient.post<
		SuccessResponse<{
			dispatchId: string;
			totalCount: number;
			sentCount: number;
			failedCount: number;
		}>
	>(endpoints.sms.batch, payload);
	return response.data.data;
}

// ── Reports ──────────────────────────────────────────────────────────────────

export type SmsReportsParams = {
	period?: "day" | "week" | "month";
	dateFrom?: string;
	dateTo?: string;
};

export async function getSmsReports(params?: SmsReportsParams) {
	const response = await apiClient.get<SuccessResponse<BackendSmsReport>>(endpoints.sms.reports, {
		params,
	});
	return mapBackendReport(response.data.data);
}

// ── Detalization ─────────────────────────────────────────────────────────────

export type SmsDetalizationParams = {
	status?: string;
	dateFrom?: string;
	dateTo?: string;
	page?: number;
	limit?: number;
};

export async function getSmsDetalization(params?: SmsDetalizationParams) {
	const response = await apiClient.get<PaginatedResponse<BackendSmsDetalizationItem>>(
		endpoints.sms.detalization,
		{ params }
	);
	const data = response.data.data;
	return {
		items: data.items.map(mapBackendDetalizationItem),
		meta: data.meta,
	};
}
